/**
 * CraftKettle Cookie Consent
 *
 * GDPR / CCPA compliant consent management integrating:
 *   - Google Consent Mode v2 (Advanced) — cookieless pings when denied
 *   - Shopify Customer Privacy API — controls Web Pixels
 *   - localStorage persistence with version-based re-prompting
 *
 * Consent defaults (deny all) are set inline in theme.liquid BEFORE
 * this file loads. This file reads stored consent and calls
 * gtag('consent', 'update', ...) to upgrade if consent was given.
 */
(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────────────
  var CONSENT_VERSION = '1';
  var STORAGE_KEY = 'ck_cookie_consent';

  // ── Storage ────────────────────────────────────────────────────────

  function getStoredConsent() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (data.version !== CONSENT_VERSION) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function saveConsent(categories) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        categories: categories,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {
      // localStorage unavailable — consent still applies for this session
    }
  }

  // ── Google Consent Mode v2 ─────────────────────────────────────────
  //
  // Defaults are set inline in theme.liquid (denied + wait_for_update).
  // This function sends the 'update' command when consent changes.

  function updateGoogleConsent(categories) {
    if (typeof gtag !== 'function') return;

    gtag('consent', 'update', {
      analytics_storage: categories.analytics ? 'granted' : 'denied',
      ad_storage: categories.marketing ? 'granted' : 'denied',
      ad_user_data: categories.marketing ? 'granted' : 'denied',
      ad_personalization: categories.marketing ? 'granted' : 'denied'
    });
  }

  // ── Shopify Customer Privacy API ───────────────────────────────────
  //
  // Controls Web Pixels loaded via content_for_header.
  // API may not be available immediately — retry once if needed.

  function buildShopifyConsent(categories) {
    return {
      analytics: categories.analytics,
      marketing: categories.marketing,
      preferences: categories.essential, // functional cookies — always true when essential is true
      sale_of_data: categories.marketing  // CCPA: only allow if marketing consented
    };
  }

  function updateShopifyConsent(categories) {
    var consent = buildShopifyConsent(categories);
    var privacy = window.Shopify && window.Shopify.customerPrivacy;

    if (!privacy || typeof privacy.setTrackingConsent !== 'function') {
      setTimeout(function () {
        var p = window.Shopify && window.Shopify.customerPrivacy;
        if (p && typeof p.setTrackingConsent === 'function') {
          p.setTrackingConsent(consent, function () {});
        }
      }, 1000);
      return;
    }

    privacy.setTrackingConsent(consent, function () {});
  }

  // ── Apply Consent ──────────────────────────────────────────────────

  function applyConsent(categories) {
    var safe = {
      essential: true,
      analytics: !!categories.analytics,
      marketing: !!categories.marketing
    };

    updateGoogleConsent(safe);
    updateShopifyConsent(safe);
    saveConsent(safe);
    hideBanner();
    hidePreferences();

    document.dispatchEvent(new CustomEvent('ck:consent-updated', {
      detail: { categories: safe }
    }));
  }

  // ── Banner Visibility ──────────────────────────────────────────────

  function showBanner() {
    var el = document.getElementById('ck-cookie-consent');
    if (el) {
      el.removeAttribute('inert');
      el.setAttribute('aria-hidden', 'false');
      el.classList.add('is-visible');
    }
  }

  function hideBanner() {
    var el = document.getElementById('ck-cookie-consent');
    if (el) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
      el.classList.remove('is-visible');
    }
  }

  function showPreferences() {
    var el = document.getElementById('ck-cookie-preferences');
    if (!el) return;
    el.removeAttribute('inert');
    el.setAttribute('aria-hidden', 'false');
    el.classList.add('is-visible');

    // Pre-fill checkboxes from stored consent
    var stored = getStoredConsent();
    var analyticsBox = document.getElementById('ck-consent-analytics');
    var marketingBox = document.getElementById('ck-consent-marketing');
    if (stored) {
      if (analyticsBox) analyticsBox.checked = stored.categories.analytics;
      if (marketingBox) marketingBox.checked = stored.categories.marketing;
    } else {
      if (analyticsBox) analyticsBox.checked = false;
      if (marketingBox) marketingBox.checked = false;
    }

    // Focus trap: capture focusable elements and trap Tab key
    var focusable = el.querySelectorAll(
      'button, [href], input, [tabindex]:not([tabindex="-1"])'
    );
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    el._trapHandler = function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    el.addEventListener('keydown', el._trapHandler);
    if (first) first.focus();
  }

  function hidePreferences() {
    var el = document.getElementById('ck-cookie-preferences');
    if (!el) return;
    if (el._trapHandler) {
      el.removeEventListener('keydown', el._trapHandler);
      el._trapHandler = null;
    }
    el.setAttribute('inert', '');
    el.setAttribute('aria-hidden', 'true');
    el.classList.remove('is-visible');
  }

  // ── Public Actions ─────────────────────────────────────────────────

  function acceptAll() {
    applyConsent({ essential: true, analytics: true, marketing: true });
  }

  function rejectAll() {
    applyConsent({ essential: true, analytics: false, marketing: false });
  }

  function saveCurrentPreferences() {
    var analyticsBox = document.getElementById('ck-consent-analytics');
    var marketingBox = document.getElementById('ck-consent-marketing');
    applyConsent({
      essential: true,
      analytics: analyticsBox ? analyticsBox.checked : false,
      marketing: marketingBox ? marketingBox.checked : false
    });
  }

  // ── Event Delegation ───────────────────────────────────────────────

  function handleClick(e) {
    var target = e.target;
    while (target && target !== document) {
      if (target.hasAttribute && target.hasAttribute('data-ck-consent')) break;
      target = target.parentElement;
    }
    if (!target || !target.hasAttribute('data-ck-consent')) return;

    var action = target.getAttribute('data-ck-consent');
    switch (action) {
      case 'accept-all':
        acceptAll();
        break;
      case 'reject-all':
        rejectAll();
        break;
      case 'customize':
        showPreferences();
        break;
      case 'save-preferences':
        saveCurrentPreferences();
        break;
      case 'close-preferences':
        hidePreferences();
        break;
    }
  }

  // ── Initialization ─────────────────────────────────────────────────

  function init() {
    var stored = getStoredConsent();

    if (stored) {
      // Returning visitor with valid consent — apply silently
      updateGoogleConsent(stored.categories);
      updateShopifyConsent(stored.categories);
    } else {
      // No valid consent — show banner to all visitors regardless of region.
      // Consent defaults (deny all) are already active in theme.liquid.
      showBanner();
    }

    document.addEventListener('click', handleClick);

    // Close preferences panel on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var prefs = document.getElementById('ck-cookie-preferences');
        if (prefs && prefs.classList.contains('is-visible')) {
          hidePreferences();
        }
      }
    });
  }

  // Run init when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── Public API ─────────────────────────────────────────────────────

  window.CKConsent = {
    acceptAll: acceptAll,
    rejectAll: rejectAll,
    savePreferences: saveCurrentPreferences,
    getConsent: getStoredConsent,
    showBanner: showBanner,
    showPreferences: showPreferences
  };
})();
